import { useState } from 'react';
import { apiGet, apiPatch, apiPost, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { useModal } from '../contexts/ModalContext';

interface Report {
  id: string;
  reporterId: string;
  reporteeId: string;
  contextType: string;
  reason: string;
  description?: string;
  status: 'PENDING' | 'REVIEWED' | 'ACTIONED' | 'DISMISSED';
  createdAt: string;
  actionTaken?: string | null;
}

interface FlaggedUser {
  id: string;
  name?: string;
  email?: string;
  chargebackCount?: number;
  chargebackFlaggedAt?: string | null;
  refundCount?: number;
  lastRefundAt?: string | null;
}

interface ContentModerationFlag {
  id: string;
  userId: string;
  storagePath: string;
  mimeType: string;
  reasons: string[];
  status: string;
  flaggedAt: string;
}

interface SpecialistPoolStatus {
  id: string;
  name?: string;
  email?: string;
  poolSuspendedAt?: string | null;
}

const reportTone = (status: Report['status']) =>
  status === 'ACTIONED' ? 'good' : status === 'DISMISSED' ? 'neutral' : status === 'REVIEWED' ? 'warn' : 'bad';

function ReportsTab() {
  const { promptText } = useModal();
  const [statusFilter, setStatusFilter] = useState('');
  const { data, loading, error, reload } = useApiResource(
    () => apiGet<Report[]>(statusFilter ? `/reports?status=${statusFilter}` : '/reports'),
    [statusFilter],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const triage = async (report: Report, status: Report['status']) => {
    const actionTaken =
      status === 'ACTIONED'
        ? (await promptText({ title: 'Triage report', message: 'Action taken (shown in the audit trail):' })) ?? undefined
        : undefined;
    setBusyId(report.id);
    setActionError(null);
    try {
      await apiPatch(`/reports/${report.id}`, { status, actionTaken });
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="section-toolbar">
        <label htmlFor="report-status">Status</label>
        <select id="report-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          <option value="PENDING">Pending</option>
          <option value="REVIEWED">Reviewed</option>
          <option value="ACTIONED">Actioned</option>
          <option value="DISMISSED">Dismissed</option>
        </select>
        <button className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      <DataTable
        id="trust-safety-reports"
        rows={data}
        loading={loading}
        empty="No reports."
        noun="reports"
        getRowKey={(r) => r.id}
        columns={[
          { key: 'reportee', header: 'Reported user', value: (r) => r.reporteeId },
          { key: 'reporter', header: 'Reporter', value: (r) => r.reporterId },
          { key: 'context', header: 'Context', value: (r) => r.contextType, filter: 'select' },
          { key: 'reason', header: 'Reason', value: (r) => r.reason, filter: 'select' },
          { key: 'description', header: 'Description', value: (r) => r.description || '\u2014' },
          {
            key: 'status',
            header: 'Status',
            value: (r) => r.status,
            filter: 'select',
            render: (r) => <StatusBadge label={r.status} tone={reportTone(r.status)} />,
          },
          {
            key: 'filed',
            header: 'Filed',
            value: (r) => r.createdAt,
            render: (r) => new Date(r.createdAt).toLocaleString(),
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (r) => (
              <div className="row-actions">
                <button className="btn" disabled={busyId === r.id} onClick={() => triage(r, 'REVIEWED')}>
                  Mark reviewed
                </button>
                <button className="btn btn-primary" disabled={busyId === r.id} onClick={() => triage(r, 'ACTIONED')}>
                  Actioned
                </button>
                <button className="btn" disabled={busyId === r.id} onClick={() => triage(r, 'DISMISSED')}>
                  Dismiss
                </button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

function TrustSafetyFlagsTab() {
  const { data, loading, error, reload } = useApiResource(
    () => apiGet<{ success: boolean; users: FlaggedUser[] }>('/payments/admin/trust-safety-flags'),
    [],
  );

  return (
    <div>
      <div className="section-toolbar">
        <button className="btn" onClick={reload}>
          Refresh
        </button>
      </div>
      {error && <div className="error-banner">{error}</div>}
      <DataTable
        id="trust-safety-flags"
        rows={data?.users}
        loading={loading}
        empty="No users currently flagged for chargebacks or refund frequency."
        noun="users"
        getRowKey={(u) => u.id ?? u.email ?? ''}
        columns={[
          { key: 'name', header: 'Name', value: (u) => u.name || '\u2014' },
          { key: 'email', header: 'Email', value: (u) => u.email || '\u2014' },
          { key: 'chargebacks', header: 'Chargebacks', value: (u) => u.chargebackCount ?? 0, align: 'right' },
          {
            key: 'flaggedAt',
            header: 'Flagged at',
            value: (u) => u.chargebackFlaggedAt ?? '',
            render: (u) =>
              u.chargebackFlaggedAt ? new Date(u.chargebackFlaggedAt).toLocaleDateString() : '\u2014',
          },
          { key: 'refunds', header: 'Refund count', value: (u) => u.refundCount ?? 0, align: 'right' },
          {
            key: 'lastRefund',
            header: 'Last refund',
            value: (u) => u.lastRefundAt ?? '',
            render: (u) => (u.lastRefundAt ? new Date(u.lastRefundAt).toLocaleDateString() : '\u2014'),
          },
        ]}
      />
    </div>
  );
}

function ContentModerationTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const { data, loading, error, reload } = useApiResource(
    () =>
      apiGet<ContentModerationFlag[]>(
        statusFilter ? `/content-moderation/flags?status=${statusFilter}` : '/content-moderation/flags',
      ),
    [statusFilter],
  );

  return (
    <div>
      <div className="section-toolbar">
        <label htmlFor="moderation-status">Status</label>
        <select id="moderation-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          <option value="pending_review">Pending review</option>
        </select>
        <button className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <DataTable
        id="trust-safety-moderation"
        rows={data}
        loading={loading}
        empty="No content-moderation flags."
        noun="flags"
        getRowKey={(f) => `${f.userId}:${f.storagePath}`}
        columns={[
          { key: 'userId', header: 'User ID', value: (f) => f.userId },
          { key: 'storagePath', header: 'Storage path', value: (f) => f.storagePath },
          { key: 'mimeType', header: 'Mime type', value: (f) => f.mimeType, filter: 'select' },
          { key: 'reasons', header: 'Reasons', value: (f) => f.reasons.join(', ') },
          {
            key: 'status',
            header: 'Status',
            value: (f) => f.status,
            filter: 'select',
            render: (f) => (
              <StatusBadge label={f.status} tone={f.status === 'pending_review' ? 'warn' : 'neutral'} />
            ),
          },
          {
            key: 'flaggedAt',
            header: 'Flagged at',
            value: (f) => f.flaggedAt,
            render: (f) => new Date(f.flaggedAt).toLocaleString(),
          },
        ]}
      />
    </div>
  );
}

interface PoolSuspendedSpecialist {
  id: string;
  name?: string;
  email?: string;
  poolSuspendedAt: string | null;
  poolSuspensionReason?: string | null;
}

interface PaginatedPoolSuspensions {
  items: PoolSuspendedSpecialist[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

function PoolSuspensionsTab() {
  const [specialistId, setSpecialistId] = useState('');
  const [specialist, setSpecialist] = useState<SpecialistPoolStatus | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [lifting, setLifting] = useState(false);
  const [liftedAt, setLiftedAt] = useState<number | null>(null);

  const [listPage, setListPage] = useState(1);
  const [liftingListId, setLiftingListId] = useState<string | null>(null);
  const {
    data: suspensions,
    loading: listLoading,
    error: listError,
    reload: reloadList,
  } = useApiResource(
    () => apiGet<PaginatedPoolSuspensions>(`/ratings/admin/pool-suspensions?page=${listPage}&limit=20`),
    [listPage],
  );

  const liftFromList = async (id: string) => {
    setLiftingListId(id);
    try {
      await apiPost(`/ratings/admin/${encodeURIComponent(id)}/lift-pool-suspension`);
      reloadList();
    } catch (err) {
      setLookupError(err instanceof ApiError ? err.message : 'Lift failed');
    } finally {
      setLiftingListId(null);
    }
  };

  const lookup = async () => {
    if (!specialistId.trim()) return;
    setLookingUp(true);
    setLookupError(null);
    setSpecialist(null);
    setLiftedAt(null);
    try {
      const user = await apiGet<SpecialistPoolStatus>(`/users/${encodeURIComponent(specialistId.trim())}`);
      setSpecialist(user);
    } catch (err) {
      setLookupError(err instanceof ApiError ? err.message : 'Lookup failed');
    } finally {
      setLookingUp(false);
    }
  };

  const lift = async () => {
    if (!specialist) return;
    setLifting(true);
    setLookupError(null);
    try {
      await apiPost(`/ratings/admin/${encodeURIComponent(specialist.id)}/lift-pool-suspension`);
      setSpecialist({ ...specialist, poolSuspendedAt: null });
      setLiftedAt(Date.now());
    } catch (err) {
      setLookupError(err instanceof ApiError ? err.message : 'Lift failed');
    } finally {
      setLifting(false);
    }
  };

  return (
    <div>
      <div className="form-card" style={{ maxWidth: 'none', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
          Currently pool-suspended specialists
        </h2>
        {listLoading && <p>Loading…</p>}
        {listError && <div className="error-banner">{listError}</div>}
        {suspensions && suspensions.items.length === 0 && <p className="form-hint">No specialists are currently pool-suspended.</p>}
        {suspensions && suspensions.items.length > 0 && (
          <DataTable
            id="trust-safety-suspensions"
            rows={suspensions.items}
            empty="No specialists are currently pool-suspended."
            noun="suspended"
            getRowKey={(s) => s.id}
            pagination={{
              page: suspensions.page,
              totalPages: suspensions.totalPages,
              total: suspensions.total,
              hasNext: suspensions.hasNext,
              hasPrev: suspensions.hasPrev,
              onPageChange: setListPage,
              noun: 'suspended',
            }}
            columns={[
              { key: 'specialist', header: 'Specialist', value: (s) => s.name || s.id },
              { key: 'email', header: 'Email', value: (s) => s.email || '\u2014' },
              {
                key: 'since',
                header: 'Suspended since',
                value: (s) => s.poolSuspendedAt ?? '',
                render: (s) => (s.poolSuspendedAt ? new Date(s.poolSuspendedAt).toLocaleString() : '\u2014'),
              },
              { key: 'reason', header: 'Reason', value: (s) => s.poolSuspensionReason || '\u2014' },
              {
                key: 'actions',
                header: 'Actions',
                render: (s) => (
                  <button
                    className="btn btn-primary"
                    disabled={liftingListId === s.id}
                    onClick={() => void liftFromList(s.id)}
                  >
                    {liftingListId === s.id ? 'Lifting…' : 'Lift suspension'}
                  </button>
                ),
              },
            ]}
          />
        )}
      </div>

      <div className="form-card" style={{ maxWidth: 'none' }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
          Rating-score-cliff pool suspension
        </h2>
        <p className="form-hint">
          Look up a specialist by ID to check whether a rating-score-cliff soft-suspension
          (BI-11 / R3.20) is currently excluding them from the matching pool, and lift it after
          review.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <input
            type="text"
            placeholder="Specialist user ID"
            value={specialistId}
            onChange={(e) => setSpecialistId(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary" disabled={lookingUp || !specialistId.trim()} onClick={() => void lookup()}>
            {lookingUp ? 'Looking up…' : 'Look up'}
          </button>
        </div>

        {lookupError && <div className="error-banner" style={{ marginTop: '1rem' }}>{lookupError}</div>}

        {specialist && (
          <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '3px', marginTop: '1rem' }}>
            <p><strong>ID:</strong> {specialist.id}</p>
            <p><strong>Name:</strong> {specialist.name || '—'}</p>
            <p><strong>Email:</strong> {specialist.email || '—'}</p>
            <p style={{ marginTop: '0.5rem' }}>
              <StatusBadge
                label={specialist.poolSuspendedAt ? `Suspended since ${new Date(specialist.poolSuspendedAt).toLocaleString()}` : 'Not suspended'}
                tone={specialist.poolSuspendedAt ? 'bad' : 'good'}
              />
            </p>
            {specialist.poolSuspendedAt && (
              <button className="btn btn-primary" disabled={lifting} style={{ marginTop: '0.75rem' }} onClick={() => void lift()}>
                {lifting ? 'Lifting…' : 'Lift pool suspension'}
              </button>
            )}
            {liftedAt && <StatusBadge label="Suspension lifted" tone="good" />}
          </div>
        )}
      </div>
    </div>
  );
}

export function TrustSafetyPage() {
  const [tab, setTab] = useState<'reports' | 'flags' | 'moderation' | 'pool-suspensions'>('reports');

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Trust &amp; Safety</h1>
          <p>Report triage and chargeback/refund-frequency flags. Being flagged is informational, not punitive.</p>
        </div>
      </div>
      <div className="tabs">
        <button className={`tab-btn ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}>
          Reports
        </button>
        <button className={`tab-btn ${tab === 'flags' ? 'active' : ''}`} onClick={() => setTab('flags')}>
          Trust &amp; Safety flags
        </button>
        <button className={`tab-btn ${tab === 'moderation' ? 'active' : ''}`} onClick={() => setTab('moderation')}>
          Content Moderation
        </button>
        <button className={`tab-btn ${tab === 'pool-suspensions' ? 'active' : ''}`} onClick={() => setTab('pool-suspensions')}>
          Pool Suspensions
        </button>
      </div>
      {tab === 'reports' ? (
        <ReportsTab />
      ) : tab === 'flags' ? (
        <TrustSafetyFlagsTab />
      ) : tab === 'moderation' ? (
        <ContentModerationTab />
      ) : (
        <PoolSuspensionsTab />
      )}
    </div>
  );
}
